import { authenticatedFetch } from '@/utils/auth';
import { createPlatformStorage } from '@/utils/indexedDBStorage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Updated Branch interface to match the actual API response structure
export interface Branch {
  branch: string;
  branch_short_name: string;
  chair_email: string;
  campaigner_id: string;
  timestamp_assigned: string;
  access_expires: string | null;
  branch_role: string | null;
  access_scope: {
    scope_description: string;
    battle_plans: Array<{
      battle_plan_id: string;
      engagements: string[];
    }>;
  } | null;
}

// Add Election interface to clarify structure
export interface Election {
  election_id: string;
  election_name: string;
  postal_votes_issued: string | null;
  polling_day: string;
  polls_open: string | null;
  polls_close: string | null;
  boundaries: string[];
  electoral_divisions?: Array<{
    boundary_id: string;
    boundary_name: string;
    boundary_type: string;
  }>;
}

// Add Campaign interface to clarify structure
export interface Campaign {
  boundary_id: string;
  boundary_name: string;
  boundary_type: string;
  code: string;
  authority_id: string;
  is_active: boolean;
}

export interface OrbatEntry {
  orbat_entry_id: string;
  branch_short_name: string;
  campaigner_id: string;
  assigned_by: string;
  branch_role: string | null;
  access_expires: string | null;
  is_revoked: boolean;
  revoked_by: string | null;
  date_revoked: string | null;
  email: string;
  name: string;
}

interface BranchData {
  // Branch metadata
  branch: Branch;
  
  // Elections for this branch
  elections: Election[] | null;
  
  // Campaigns for this branch
  campaigns: Campaign[] | null;
  
  // Current campaign selection for this branch
  selectedCampaignId: string | null;
  
  // Orbat (campaigners) for this branch
  orbat: OrbatEntry[] | null;
  
  // Cache timestamps for data freshness
  lastUpdated: {
    elections: number;
    campaigns: number;
    orbat: number;
  };
}

interface NavigationStore {
  // Global state (not branch-specific)
  branches: Branch[] | null;
  selectedBranchId: string | null;
  loading: boolean;
  error: string | null;
  
  // Share settings (user preferences)
  shareSettings: {
    accessDuration: string;
    codeExpires: string;
    numUses: string;
  };
  
  // Remember me functionality
  rememberMe: boolean;
  rememberedUsername: string | null;
  
  // Engagement filter/sort preferences (keyed by battlePlanId)
  engagementFilterSettings: {
    [battlePlanId: string]: {
      filterType: 'all' | 'unassigned' | 'assigned' | 'my-routes';
      sortType: 'number' | 'priority';
    };
  };
  
  // Branch-specific data (organized by branch)
  branchData: {
    [branchId: string]: BranchData;
  };
  
  // Selector functions (replacing computed getters)
  getSelectedBranch: () => Branch | null;
  getSelectedCampaign: () => Campaign | null;
  
  // Actions
  // Global actions
  setBranches: (branches: Branch[]) => void;
  clearExpiredBranches: () => void;
  setSelectedBranchId: (branchId: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Share settings actions
  setShareSettings: (settings: Partial<NavigationStore['shareSettings']>) => void;
  
  // Remember me actions
  setRememberMe: (rememberMe: boolean) => void;
  setRememberedUsername: (username: string | null) => void;
  clearRememberedUsername: () => void;
  
  // Engagement filter/sort actions
  setEngagementFilter: (battlePlanId: string, filterType: 'all' | 'unassigned' | 'assigned' | 'my-routes') => void;
  setEngagementSort: (battlePlanId: string, sortType: 'number' | 'priority') => void;
  getEngagementFilterSettings: (battlePlanId: string) => { filterType: 'all' | 'unassigned' | 'assigned' | 'my-routes'; sortType: 'number' | 'priority' };
  
  // Branch-specific actions
  setBranchData: (branchId: string, data: Partial<BranchData>) => void;
  setElections: (branchId: string, elections: Election[]) => void;
  setCampaigns: (branchId: string, campaigns: Campaign[]) => void;
  setOrbat: (branchId: string, orbat: OrbatEntry[]) => void;
  setSelectedCampaignId: (branchId: string, campaignId: string | null) => void;
  
  // Complex actions
  changeBranch: (branch: Branch) => Promise<void>;
  clearSelectedBranch: () => void;
  clearAllData: () => void;
  fetchBranches: () => Promise<Branch[]>;
  fetchElections: (branchId: string) => Promise<Election[]>;
  fetchCampaigns: (branchId: string) => Promise<Campaign[] | null>;
  fetchOrbat: (branchId: string) => Promise<OrbatEntry[]>;
  clearBranchData: (branchId: string) => void;
  redeemAccessToken: (token: string) => Promise<{ success: boolean; accessScope?: any; branchId?: string }>;
  
  // Utility actions
  getBranchData: (branchId: string) => BranchData | undefined;
  getElections: (branchId: string) => Election[] | null;
  getCampaigns: (branchId: string) => Campaign[] | null;
  getOrbat: (branchId: string) => OrbatEntry[] | null;
  getElectionForCampaign: (campaign: Campaign, branchId: string) => Election | null;
}

// Helper function to deduplicate branches based on priority rules:
// 1. Entries with roles over those without roles
// 2. Latest expiry date when roles are equal
const deduplicateBranches = (branches: Branch[]): Branch[] => {
  const branchGroups = new Map<string, Branch[]>();
  
  // Group branches by branch_short_name
  branches.forEach(branch => {
    const key = branch.branch_short_name;
    if (!branchGroups.has(key)) {
      branchGroups.set(key, []);
    }
    branchGroups.get(key)!.push(branch);
  });

  // For each group, select the best branch
  const result = Array.from(branchGroups.values()).map(group => {
    if (group.length === 1) return group[0];
    
    // Sort by priority: role first, then expiry date
    const sortedGroup = group.sort((a, b) => {
      // First priority: branches with roles over those without
      const aHasRole = !!a.branch_role;
      const bHasRole = !!b.branch_role;
      
      if (aHasRole && !bHasRole) return -1;
      if (!aHasRole && bHasRole) return 1;
      
      // Now, prefer latest expiry date
      if (a.access_expires === null && b.access_expires === null) return 0;
      if (a.access_expires === null) return -1;
      if (b.access_expires === null) return 1;
      return new Date(b.access_expires).getTime() - new Date(a.access_expires).getTime();
    });
    
    const selectedBranch = sortedGroup[0];
    if (selectedBranch) {      
      return selectedBranch;
    }
    return undefined;
  }).filter((branch): branch is Branch => branch !== undefined);
  
  return result;
};

export const useNavigationStore = create<NavigationStore>()(
  persist(
    (set, get) => ({
      // Initial state
      branches: null,
      selectedBranchId: null,
      loading: false,
      error: null,
      branchData: {},
      shareSettings: {
        accessDuration: '12 hours',
        codeExpires: '1 hour',
        numUses: '1',
      },
      rememberMe: true, // Default to true (remember me is ticked by default)
      rememberedUsername: null,
      engagementFilterSettings: {},

      // Selector functions (replacing computed getters)
      getSelectedBranch: () => {
        const state = get();
        const { selectedBranchId, branches } = state;
        if (!selectedBranchId || !branches) return null;
        const now = new Date();
        const foundBranch = branches.find(
          b =>
            b.branch_short_name === selectedBranchId &&
            (b.access_expires === null || new Date(b.access_expires) > now)
        );
        return foundBranch || null;
      },

      getSelectedCampaign: () => {
        const state = get();
        const { selectedBranchId, branchData } = state;
        if (!selectedBranchId) return null;
        const branchDataItem = branchData[selectedBranchId];
        if (!branchDataItem?.selectedCampaignId) return null;
        return branchDataItem.campaigns?.find(c => c.boundary_id === branchDataItem.selectedCampaignId) || null;
      },

      // Global actions
      setBranches: (branches) => {
        set({ branches });
      },
      clearExpiredBranches: () => {
        const { branches } = get();
        if (!branches) return;
        
        const now = new Date();
        const activeBranches = branches.filter(
          branch => !branch.access_expires || new Date(branch.access_expires) > now
        );
        
        // Only update if there are expired branches to remove
        if (activeBranches.length !== branches.length) {
          set({ branches: activeBranches });
        }
      },
      setSelectedBranchId: (selectedBranchId) => {
        set({ selectedBranchId });
      },
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      // Share settings actions
      setShareSettings: (settings) => set((state) => ({
        shareSettings: { ...state.shareSettings, ...settings }
      })),

      // Remember me actions
      setRememberMe: (rememberMe) => set({ rememberMe }),
      setRememberedUsername: (rememberedUsername) => set({ rememberedUsername }),
      clearRememberedUsername: () => set({ rememberedUsername: null }),

      // Engagement filter/sort actions
      setEngagementFilter: (battlePlanId, filterType) => set((state) => ({
        engagementFilterSettings: {
          ...state.engagementFilterSettings,
          [battlePlanId]: {
            ...state.engagementFilterSettings[battlePlanId],
            filterType,
            sortType: state.engagementFilterSettings[battlePlanId]?.sortType || 'priority',
          },
        },
      })),

      setEngagementSort: (battlePlanId, sortType) => set((state) => ({
        engagementFilterSettings: {
          ...state.engagementFilterSettings,
          [battlePlanId]: {
            ...state.engagementFilterSettings[battlePlanId],
            filterType: state.engagementFilterSettings[battlePlanId]?.filterType || 'all',
            sortType,
          },
        },
      })),

      getEngagementFilterSettings: (battlePlanId) => {
        const state = get();
        const settings = state.engagementFilterSettings[battlePlanId];
        if (settings) {
          return settings;
        }
        // Return default settings if not found
        return {
          filterType: 'all' as const,
          sortType: 'priority' as const,
        };
      },

      // Branch-specific actions
      setBranchData: (branchId, data) => set((state) => ({
        branchData: {
          ...state.branchData,
          [branchId]: {
            ...state.branchData[branchId],
            ...data,
          } as BranchData,
        },
      })),

      setElections: (branchId, elections) => {
        const { setBranchData } = get();
        const currentData = get().branchData[branchId];
        setBranchData(branchId, {
          elections,
          lastUpdated: {
            elections: Date.now(),
            campaigns: currentData?.lastUpdated?.campaigns || 0,
            orbat: currentData?.lastUpdated?.orbat || 0,
          },
        });
      },

      setCampaigns: (branchId, campaigns) => {
        const { setBranchData } = get();
        const currentData = get().branchData[branchId];
        setBranchData(branchId, {
          campaigns,
          lastUpdated: {
            elections: currentData?.lastUpdated?.elections || 0,
            campaigns: Date.now(),
            orbat: currentData?.lastUpdated?.orbat || 0,
          },
        });
      },

      setOrbat: (branchId: string, orbat: OrbatEntry[]) => {
        const { setBranchData } = get();
        const currentData = get().branchData[branchId];
        setBranchData(branchId, {
          orbat,
          lastUpdated: {
            elections: currentData?.lastUpdated?.elections || 0,
            campaigns: currentData?.lastUpdated?.campaigns || 0,
            orbat: Date.now(),
          },
        });
      },

      setSelectedCampaignId: (branchId, selectedCampaignId) => {
        const { setBranchData } = get();
        setBranchData(branchId, { selectedCampaignId });
      },

      // Complex actions
      changeBranch: async (branch) => {
        const { setSelectedBranchId, clearBranchData } = get();
        
        // Clear previous branch data
        const currentBranchId = get().selectedBranchId;
        if (currentBranchId && currentBranchId !== branch.branch_short_name) {
          clearBranchData(currentBranchId);
        }
        
        // Set new branch
        setSelectedBranchId(branch.branch_short_name);
        
        // Initialize branch data if it doesn't exist
        const { branchData, setBranchData } = get();
        if (!branchData[branch.branch_short_name]) {
          setBranchData(branch.branch_short_name, {
            branch,
            elections: null,
            campaigns: null,
            selectedCampaignId: null,
            orbat: null,
            lastUpdated: {
              elections: 0,
              campaigns: 0,
              orbat: 0,
            },
          });
        }
      },

      clearSelectedBranch: () => {
        const { setSelectedBranchId, clearBranchData } = get();
        const currentBranchId = get().selectedBranchId;
        if (currentBranchId) {
          clearBranchData(currentBranchId);
        }
        setSelectedBranchId(null);
      },

      clearAllData: () => {
        set({
          branches: null,
          selectedBranchId: null,
          loading: false,
          error: null,
          branchData: {},
          rememberedUsername: null, // Clear remembered username on sign out
        });
      },

      fetchBranches: async () => {
        const { setBranches, setError, selectedBranchId } = get();
        
        try {
          const response = await authenticatedFetch(
            selectedBranchId || '', 
            '/branches/list-my-access', 
            { method: 'GET' }, 
            'groundforce'
          );
          
          if (!response.ok) {
            const errorMessage = `HTTP error! status: ${response.status}`;
            setError(errorMessage);
            throw new Error(errorMessage);
          }
          
          const branches: Branch[] = await response.json();
       
          const deduplicatedBranches = deduplicateBranches(branches);

          setBranches(deduplicatedBranches);
          setError(null); // Clear any previous errors on success
          return deduplicatedBranches;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load branches';
          setError(errorMessage);
          console.error('[NavigationStore] fetchBranches: error', err);
          // Throw the error so useBackgroundFetch can catch it and show proper error state
          throw err;
        }
      },

      fetchElections: async (branchId) => {
        const { setElections, setError } = get();
        
        try {
          const response = await authenticatedFetch(
            branchId, 
            '/elections/get-elections', 
            { method: 'GET' }, 
            'groundforce'
          );
          
          if (!response.ok) {
            const errorMessage = `HTTP error! status: ${response.status}`;
            setError(errorMessage);
            throw new Error(errorMessage);
          }
          
          const elections: Election[] = await response.json();
          setElections(branchId, elections);
          setError(null); // Clear any previous errors on success
          return elections;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load elections';
          setError(errorMessage);
          console.error('[NavigationStore] fetchElections: error', err);
          // Throw the error so useBackgroundFetch can catch it and show proper error state
          throw err;
        }
      },

      fetchCampaigns: async (branchId) => {
        if (!branchId || branchId === '') return null;

        const { setCampaigns, setError, getElections } = get();
        
        try {
          // First get elections if not already available
          let elections = getElections(branchId);
          if (!elections || elections.length === 0) {
            elections = await get().fetchElections(branchId);
          }
          
          // Extract campaigns from electoral_divisions in each election
          const allCampaigns: Campaign[] = [];
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
          
          for (const election of elections || []) {
            // Determine if election is active based on polling_day
            const pollingDay = new Date(election.polling_day);
            pollingDay.setHours(0, 0, 0, 0);
            const isElectionActive = pollingDay >= today;
            
            // Use electoral_divisions from the election if available
            if (election.electoral_divisions && election.electoral_divisions.length > 0) {
              const campaigns: Campaign[] = election.electoral_divisions.map(division => ({
                boundary_id: division.boundary_id,
                boundary_name: division.boundary_name,
                boundary_type: division.boundary_type,
                code: division.boundary_id, // Use boundary_id as code if not provided
                authority_id: '', // Not available in electoral_divisions, set empty
                is_active: isElectionActive, // Set based on whether polling day is today or in the future
              }));
              allCampaigns.push(...campaigns);
            }
          }
          
          setCampaigns(branchId, allCampaigns);
          setError(null); // Clear any previous errors on success
          return allCampaigns;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load campaigns';
          setError(errorMessage);
          console.error('[NavigationStore] fetchCampaigns: error', err);
          // Throw the error so useBackgroundFetch can catch it and show proper error state
          throw err;
        }
      },

      fetchOrbat: async (branchId: string) => {
        if (!branchId || branchId === '') return [];

        const { setOrbat, setError } = get();
        
        try {
          const response = await authenticatedFetch(
            branchId,
            '/orbat/get-orbat?include_expired=true',
            { method: 'GET' },
            'query' // Not 'groundforce'
          );
          
          if (!response.ok) {
            const errorMessage = `HTTP error! status: ${response.status}`;
            setError(errorMessage);
            throw new Error(errorMessage);
          }
          
          const orbat: OrbatEntry[] = await response.json();
          setOrbat(branchId, orbat);
          setError(null); // Clear any previous errors on success
          return orbat;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load orbat';
          setError(errorMessage);
          console.error('[NavigationStore] fetchOrbat: error', err);
          // Throw the error so useBackgroundFetch can catch it and show proper error state
          throw err;
        }
      },

      clearBranchData: (branchId) => {
        const { branchData } = get();
        const newBranchData = { ...branchData };
        delete newBranchData[branchId];
        set({ branchData: newBranchData });
      },

      // Helper: redeem access token
      redeemAccessToken: async (token: string): Promise<{ success: boolean; accessScope?: any; branchId?: string }> => {
        try {
          if (token) {
            // Call the API to redeem the token
            const response = await authenticatedFetch(null, '/access-grants/redeem-token', {
              method: 'POST',
              body: JSON.stringify({ token }),
              headers: { 'Content-Type': 'application/json' },
            }, 'groundforce');
            
            // Check if the response is successful
            if (!response.ok) {
              const errorMessage = `HTTP error! status: ${response.status}`;
              console.error('[NavigationStore] redeemAccessToken: error', errorMessage);
              return { success: false };
            }
            
            // Parse the response to get access scope and branch information
            const responseData = await response.json();
            return { 
              success: true, 
              accessScope: responseData.access_scope || null,
              branchId: responseData.branch_short_name || null
            };
          }
          return { success: false };
        } catch (e) {
          // Log error and return false to indicate failure
          const errorMessage = e instanceof Error ? e.message : 'Unknown error';
          console.error('[NavigationStore] redeemAccessToken: error', errorMessage, e);
          return { success: false };
        }
      },

      // Utility actions
      getBranchData: (branchId) => get().branchData[branchId],
      getElections: (branchId) => get().branchData[branchId]?.elections || null,
      getCampaigns: (branchId) => get().branchData[branchId]?.campaigns || null,
      getOrbat: (branchId) => get().branchData[branchId]?.orbat || null,
      
      getElectionForCampaign: (campaign, branchId) => {
        const elections = get().getElections(branchId);
        return elections?.find(election => 
          election.boundaries.includes(campaign.boundary_id)
        ) || null;
      },
    }),
    {
      name: 'navigation-storage',
      storage: createJSONStorage(() => createPlatformStorage()),
      partialize: (state) => ({
        branches: state.branches,
        selectedBranchId: state.selectedBranchId,
        branchData: state.branchData,
        shareSettings: state.shareSettings,
        rememberMe: state.rememberMe,
        rememberedUsername: state.rememberedUsername,
        engagementFilterSettings: state.engagementFilterSettings,
      }),
    }
  )
);
